import React, { useMemo } from "react";
import "./ContactUsCaRrds.css";
import { FiPhone, FiMail } from "react-icons/fi";
import { scrollToElementById } from "../../../../utils/scrollToId";
import { resolveMediaUrl } from "../../../../utils/media";

import supportIcon from "../../../../assets/images/contactus/co1.svg";
import requestIcon from "../../../../assets/images/contactus/co2.svg";
import locationIcon from "../../../../assets/images/contactus/co3.svg";

const defaultIcons = [supportIcon, requestIcon, locationIcon];

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceDetailsPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return coalesceDetailsPayload(tryParseJsonObject(raw) || {});
  }
  if (typeof raw !== "object") return null;

  const mergeDetailsWrapper = (node, into) => {
    const wrap = node?.details ?? node?.Details;
    if (!wrap || typeof wrap !== "object") return into;
    if (wrap.data && typeof wrap.data === "object" && !Array.isArray(wrap.data)) {
      return { ...into, ...wrap.data };
    }
    return { ...into, ...wrap };
  };

  let base = { ...raw };
  base = mergeDetailsWrapper(raw, base);
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...base, ...raw.data };
    base = mergeDetailsWrapper(raw.data, base);
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return mergeDetailsWrapper(base, base);
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasDetailsPayload = (p) => {
  if (!p || typeof p !== "object") return false;
  return Object.keys(p).some((k) => /^card\d+/i.test(k));
};

const collectPhones = (payload, cardIndex) => {
  const phones = [];
  for (let j = 1; j <= 12; j++) {
    const v = pickString(payload[`card${cardIndex}Phone${j}`], payload[`card${cardIndex}phone${j}`]);
    if (v) phones.push(v);
  }
  return phones;
};

const collectLocations = (payload, cardIndex) => {
  const locations = [];
  for (let j = 1; j <= 12; j++) {
    const v = pickString(payload[`card${cardIndex}Location${j}`], payload[`card${cardIndex}location${j}`]);
    if (v) locations.push(v);
  }
  return locations;
};

const buildCardsFromPayload = (payload) => {
  const out = [];
  for (let i = 1; i <= 8; i++) {
    const title = pickString(payload[`card${i}Title`], payload[`card${i}Heading`]);
    const buttonText = pickString(payload[`card${i}ButtonText`], payload[`card${i}buttonText`]);
    const scrollId =
      pickString(
        payload[`card${i}FormScrollTargetId`],
        payload[`card${i}formScrollTargetId`]
      ) || "contact-form";
    const phones = collectPhones(payload, i);
    const email = pickString(payload[`card${i}Email`], payload[`card${i}email`]);
    const locations = collectLocations(payload, i);
    const iconResolved =
      resolveMediaUrl(payload[`card${i}Icon`] ?? payload[`card${i}Image`] ?? payload[`card${i}icon`]) ||
      "";
    const iconFallback = defaultIcons[(i - 1) % defaultIcons.length];

    if (buttonText) {
      out.push({
        id: i,
        title: title || buttonText,
        type: "button",
        icon: iconResolved || iconFallback,
        iconFallback,
        buttonText,
        scrollId,
      });
      continue;
    }

    if (phones.length > 0 || email) {
      out.push({
        id: i,
        title: title || "Contact",
        type: "contact",
        icon: iconResolved || iconFallback,
        iconFallback,
        phones,
        email,
      });
      continue;
    }

    if (locations.length > 0) {
      out.push({
        id: i,
        title: title || "Location",
        type: "location",
        icon: iconResolved || iconFallback,
        iconFallback,
        locations,
      });
    }
  }
  return out;
};

const ContactUsCaRrds = ({ content }) => {
  const cards = useMemo(() => {
    const payload = coalesceDetailsPayload(content);
    if (!hasDetailsPayload(payload) || !payload) return [];
    return buildCardsFromPayload(payload);
  }, [content]);

  const handleRequestCall = (scrollId) => {
    scrollToElementById(scrollId || "contact-form");
  };

  if (!cards.length) return null;

  return (
    <section className="contactCards">
      <div className="contactCards-grid">
        {cards.map((card) => (
          <div className="contactCards-card" key={card.id}>
            <div className="contactCards-iconWrap">
              <img
                src={card.icon}
                alt={card.title}
                className="contactCards-icon"
                onError={(e) => {
                  if (e.currentTarget.src !== card.iconFallback) {
                    e.currentTarget.src = card.iconFallback;
                  }
                }}
              />
            </div>

            <h3>{card.title}</h3>

            {card.type === "contact" && (
              <div className="contactCards-content">
                {card.phones.length > 0 ? (
                  <div className="contactCards-row contactCards-rowTop">
                    <FiPhone className="contactCards-smallIcon" />
                    <div className="contactCards-textGroup">
                      {card.phones.map((phone, index) => (
                        <a key={index} href={`tel:${phone.replace(/\s+/g, "")}`}>
                          {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}

                {card.email ? (
                  <div className="contactCards-row">
                    <FiMail className="contactCards-smallIcon" />
                    <a href={`mailto:${card.email}`} className="contactCards-email">
                      {card.email}
                    </a>
                  </div>
                ) : null}
              </div>
            )}

            {card.type === "button" && (
              <div className="contactCards-btnWrap">
                <button
                  type="button"
                  className="buttongyp"
                  onClick={() => handleRequestCall(card.scrollId)}
                >
                  {card.buttonText}
                </button>
              </div>
            )}

            {card.type === "location" && (
              <div className="contactCards-locationList">
                {card.locations.map((location, index) => (
                  <p key={index}>{location}</p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default ContactUsCaRrds;
